import React, { useEffect, useState } from 'react';
import { Referral, referralHasMissingFields } from '../services/apiService';
import { Modal } from './Modal';
import { Button } from './Button';
import FeatherIcon from 'feather-icons-react';

export type SRClarificationTemplate = 'missing_fields' | 'custom';

interface SRClarificationModalProps {
  referral: Referral;
  isOpen: boolean;
  onClose: () => void;
  onSent: () => void;
  loadMissingFieldsDraft: (referralId: string) => Promise<string>;
  onSend: (
    referralId: string,
    body: string,
    awaitingReplyNote?: string,
  ) => Promise<void>;
}

export const SRClarificationModal: React.FC<SRClarificationModalProps> = ({
  referral,
  isOpen,
  onClose,
  onSent,
  loadMissingFieldsDraft,
  onSend,
}) => {
  const hasMissingFields = referralHasMissingFields(referral);
  const [template, setTemplate] = useState<SRClarificationTemplate>(
    hasMissingFields ? 'missing_fields' : 'custom',
  );
  const [message, setMessage] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setError(null);
    setInternalNote('');
    setTemplate(hasMissingFields ? 'missing_fields' : 'custom');
    setMessage('');
  }, [isOpen, hasMissingFields, referral.id]);

  useEffect(() => {
    if (!isOpen || template !== 'missing_fields' || !hasMissingFields) {
      return;
    }

    let cancelled = false;
    setLoadingDraft(true);
    setError(null);

    loadMissingFieldsDraft(referral.id)
      .then((body) => {
        if (!cancelled) {
          setMessage(body);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load draft message.');
          setTemplate('custom');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingDraft(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, template, hasMissingFields, referral.id, loadMissingFieldsDraft]);

  const handleClose = () => {
    if (sending) return;
    onClose();
  };

  const handleSend = async () => {
    const body = message.trim();
    if (!body) {
      setError('Please enter a message for the GP.');
      return;
    }

    setSending(true);
    setError(null);
    try {
      await onSend(referral.id, body, internalNote.trim() || undefined);
      onSent();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send clarification request.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isLoading={sending || loadingDraft}>
      <div className="modal-header">
        <h2>Request Clarification</h2>
        <Button variant="icon" onClick={handleClose} title="Close" disabled={sending}>
          <FeatherIcon icon="x" size={20} />
        </Button>
      </div>
      <div className="modal-body">
        <p style={{ marginBottom: '12px', fontSize: '14px', color: '#555' }}>
          Send a message to the referring GP for{' '}
          <strong>
            {referral.patient_first_name} {referral.patient_last_name}
          </strong>
          {referral.sr_referral_number ? ` (${referral.sr_referral_number})` : ''}. The referral
          will move to Awaiting Reply after the message is sent successfully.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
            <input
              type="radio"
              name="clarification-template"
              checked={template === 'missing_fields'}
              disabled={!hasMissingFields || sending || loadingDraft}
              onChange={() => setTemplate('missing_fields')}
            />
            <span>
              Missing information
              {!hasMissingFields && (
                <span style={{ color: '#888', display: 'block', fontSize: '13px' }}>
                  No missing fields on this referral
                </span>
              )}
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="radio"
              name="clarification-template"
              checked={template === 'custom'}
              disabled={sending || loadingDraft}
              onChange={() => setTemplate('custom')}
            />
            <span>Custom question</span>
          </label>
        </div>

        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={8}
          className="form-control"
          placeholder={loadingDraft ? 'Loading draft...' : 'Enter your message to the GP...'}
          disabled={sending || loadingDraft}
          style={{ width: '100%', resize: 'vertical' }}
        />

        <label style={{ display: 'block', marginTop: '12px', fontSize: '14px', fontWeight: 600 }}>
          Internal note (optional)
        </label>
        <textarea
          value={internalNote}
          onChange={(e) => setInternalNote(e.target.value)}
          rows={2}
          className="form-control"
          placeholder="Note shown in the Awaiting Reply queue..."
          disabled={sending || loadingDraft}
          style={{ width: '100%', resize: 'vertical', marginTop: '4px' }}
        />

        {error && (
          <div style={{ color: '#c62828', marginTop: '8px', fontSize: '14px' }}>
            {error}
          </div>
        )}
      </div>
      <div
        className="modal-footer"
        style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '1rem' }}
      >
        <Button variant="secondary" onClick={handleClose} disabled={sending}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSend}
          isLoading={sending}
          disabled={sending || loadingDraft}
        >
          Send to GP
        </Button>
      </div>
    </Modal>
  );
};
